import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Calendar,
  Building2,
  Clock,
  GripVertical,
  MoreVertical,
  ThumbsUp,
  ThumbsDown,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { Opportunity } from '../../types/internal-opportunity.types';
import { DECISION_CONFIG } from '../../types/internal-opportunity.types';

interface KanbanCardProps {
  opportunity: Opportunity;
  isDragging?: boolean;
  onDecision?: (id: number, decision: 'go' | 'no_go') => void;
}

export function KanbanCard({ opportunity, isDragging, onDecision }: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
  } = useDraggable({ id: opportunity.id });

  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    touchAction: 'none',
  };

  const decisionConfig = opportunity.decision
    ? DECISION_CONFIG[opportunity.decision]
    : null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  };

  const getDeadlineUrgency = () => {
    if (opportunity.daysUntilDeadline === undefined) return null;
    if (opportunity.daysUntilDeadline < 0) {
      return { text: 'Dépassée', className: 'text-destructive bg-destructive/10' };
    }
    if (opportunity.daysUntilDeadline <= 7) {
      return { text: `${opportunity.daysUntilDeadline}j`, className: 'text-amber-700 bg-amber-100' };
    }
    return null;
  };

  const deadlineUrgency = getDeadlineUrgency();
  const buyerName = opportunity.buyer?.name || opportunity.buyerName;
  const canDecide = opportunity.decision !== 'go' && opportunity.decision !== 'no_go';

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`transition-shadow ${
        isDragging ? 'opacity-50 shadow-lg rotate-2' : 'hover:shadow-md hover:border-copper/50'
      }`}
    >
      <CardContent className="p-3 space-y-2">
        {/* Handle + Menu + Badges */}
        <div className="flex items-start gap-1">
          <div
            {...attributes}
            {...listeners}
            className="mt-1 px-1 py-2 -ml-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap gap-1 mb-1">
              {decisionConfig && (
                <Badge
                  className={`text-xs ${decisionConfig.color} ${decisionConfig.bgColor}`}
                >
                  {decisionConfig.label}
                </Badge>
              )}
              {deadlineUrgency && (
                <Badge className={`text-xs ${deadlineUrgency.className}`}>
                  <Clock className="w-3 h-3 mr-1" />
                  {deadlineUrgency.text}
                </Badge>
              )}
            </div>
            <Link to={`/tm/opportunities/${opportunity.id}`}>
              <h4 className="text-sm font-medium leading-tight line-clamp-2 hover:text-copper transition-colors">
                {opportunity.shortTitle || opportunity.title}
              </h4>
            </Link>
          </div>

          {/* Menu contextuel */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0">
                <MoreVertical className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/tm/opportunities/${opportunity.id}`}>
                  <Eye className="mr-2 h-4 w-4" />
                  Voir les détails
                </Link>
              </DropdownMenuItem>
              {opportunity.sourceUrl && (
                <DropdownMenuItem asChild>
                  <a
                    href={opportunity.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Voir l'avis original
                  </a>
                </DropdownMenuItem>
              )}
              {onDecision && canDecide && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDecision(opportunity.id, 'go')}
                    className="text-green-600"
                  >
                    <ThumbsUp className="mr-2 h-4 w-4" />
                    Décision GO
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onDecision(opportunity.id, 'no_go')}
                    className="text-red-600"
                  >
                    <ThumbsDown className="mr-2 h-4 w-4" />
                    Décision NO-GO
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Acheteur */}
        {buyerName && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Building2 className="w-3 h-3 shrink-0" />
            <span className="truncate">{buyerName}</span>
          </div>
        )}

        {/* Date limite + lots */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {opportunity.submissionDeadline && (
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>{formatDate(opportunity.submissionDeadline)}</span>
            </div>
          )}
          {opportunity.isMultiLot && opportunity.lotsCount > 1 && (
            <Badge variant="outline" className="text-xs h-5">
              {opportunity.lotsCount} lots
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
